// import React from 'react';
// import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { HashLink } from 'react-router-hash-link';
import { styled } from '@mui/material/styles';
import { AffinityDto } from 'models/dtoTypes';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';

const PREFIX = '_sdgScoreClob';

const classes = {
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  anchorTitle: `${PREFIX}-anchorTitle`,
  dimension: `${PREFIX}-dimension`,
  affinityContainer: `${PREFIX}-affinityContainer`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'stretch',

  [`& .${classes.affinityContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    padding: 10,
    '& .dot': {
      verticalAlign: 'middle',
    },
    '& .circle': {
      verticalAlign: 'middle',
      border: '1px solid #707070',
    },
  },

  [`& .${classes.content}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: 7,
    '& a': {
      textDecorationColor: '#084fa3',
    },
    '& a:active': {
      textDecorationColor: '#3544d0',
    },
  },

  [`& .${classes.title}`]: {
    flex: 1,
    color: '#1c1c1c',
  },
  [`& .${classes.anchorTitle}`]: {
    flex: 1,
    color: '#084fa3',
  },
  [`& .${classes.dimension}`]: {
    alignSelf: 'center',
    width: 100,
    fontSize: 12,
    textTransform: 'capitalize',
    color: 'rgba(0,0,0,0.5)',
  },
}));

export interface ISdgData {
  sdgId?: string;
  anchor?: string;
  itemNumber?: number;
  title?: string;
  weight?: 'bold' | 'regular';
  iccDimension?: string;
  affinity?: AffinityDto;
  affinityColor?: string; // specific colour
  withBorder?: boolean;
  withBg?: boolean;
  content?: string;
  size?: 'default' | 'small';
}

const generateColor = (category: string): string => {
  switch (category) {
    case 'negative':
      return '#ff7c8f';
    case 'positive':
      return '#23c8a7';
    case 'neutral':
      return '#ffffff';
    case 'warning':
      return '#e6b12e';
    default:
      return '#666666';
  }
};

export default function SdgScoreClob(props: ISdgData): JSX.Element {
  const {
    sdgId,
    itemNumber,
    title,
    size = 'default',
    anchor,
    iccDimension,
    affinity,
    affinityColor,
    withBorder,
    weight = 'bold',
    withBg,
    content,
  } = props;

  const { t } = useTranslation('default');

  const dimensionMapper = {
    'human-social': t('human-social'),
    environmental: t('environmental'),
    economic: t('economic'),
    'socio-economic': t('socioeconomic'),
  };

  const indicatorStyles =
    size === 'default'
      ? {
          color: 'white',
          fontSize: 12,
          width: 30,
          height: 30,
          borderRadius: 15,
        }
      : {
          color: 'white',
          width: 20,
          fontSize: 9,
          height: 20,
          borderRadius: 10,
        };
  const titleStyles =
    size === 'default'
      ? {
          fontSize: 16,
          fontWeight: weight,
        }
      : {
          fontSize: 12,
          fontWeight: weight,
        };
  const renderAffinity = () => {
    const color = affinityColor ? affinityColor : generateColor(affinity || '');
    return (
      <div
        className={classes.affinityContainer}
        style={{
          backgroundColor: withBg ? '#121212' : 'transparent',
          padding: size === 'default' ? 10 : 5,
        }}
      >
        <div
          className={affinity === 'neutral' ? 'circle' : 'dot'}
          style={{
            ...indicatorStyles,
            backgroundColor: color,
            textAlign: 'center',
            alignContent: 'center',
          }}
        >
          {content}
        </div>
      </div>
    );
  };

  const scrollWithOffset = (el: HTMLElement) => {
    const yCoordinate: number = el.getBoundingClientRect().top + window.pageYOffset;
    const yOffset = -80;
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
  };

  const renderTitle = () => {
    if (anchor) {
      return (
        <div>
          {/* @ts-ignore */}
          <HashLink smooth to={`#${anchor}`} scroll={scrollWithOffset}>
            <div className={classes.anchorTitle} style={titleStyles}>
              {title}
            </div>
          </HashLink>
        </div>
      );
    } else {
      return (
        <div className={classes.title} style={titleStyles}>
          {title}
        </div>
      );
    }
  };

  const renderContent = () => {
    return (
      <div className={classes.content}>
        <div style={{ display: 'flex' }}>
          {itemNumber !== undefined && (
            <div style={{ width: 30, marginLeft: 10 }}>{itemNumber}.</div>
          )}
          <div style={{ flex: 1 }}>{renderTitle()}</div>
        </div>
      </div>
    );
  };

  const renderRecord = () => {
    return (
      <Root id={sdgId}>
        {(affinity || affinityColor) && renderAffinity()}
        {(title || iccDimension) && renderContent()}
        {iccDimension && (
          <div className={classes.dimension}>
            {get(dimensionMapper, iccDimension) || iccDimension}
          </div>
        )}
      </Root>
    );
  };

  return (
    <Box
      component="div"
      sx={{
        mt: withBorder ? 1 : 0,
        mb: withBorder ? 1 : 0,
        border: withBorder ? '2px solid #808080' : null,
      }}
    >
      {renderRecord()}
    </Box>
  );
}
